import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '../lib/api';
import { useAuth } from '../contexts/useAuth';
import { format, startOfMonth, endOfMonth, subMonths, eachDayOfInterval, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
    Download,
    Calendar,
    Filter,
    Loader2,
    BarChart2,
    PieChart,
    Clock,
    TrendingUp,
    Building2,
    X,
    ChevronRight,
} from 'lucide-react';
import { cn, hoursToDays, getStatusBadgeClass, getStatusLabel } from '../lib/utils';
import type { ApiResponse, TimeEntry } from '../types';

interface ReportStats {
    totalHours: number;
    approvedHours: number;
    billedHours: number;
    pendingHours: number;
    byProject: {
        projectId: string;
        projectCode: string;
        projectName: string;
        clientName: string;
        hours: number;
        approvedHours: number;
    }[];
    byUser: {
        userId: string;
        userName: string;
        hours: number;
        approvedHours: number;
        daysWorked: number;
    }[];
    byClient: {
        clientId: string;
        clientName: string;
        hours: number;
        projectCount: number;
    }[];
    dailyBreakdown: {
        date: string;
        hours: number;
    }[];
}

export function Reports() {
    const api = useApi();
    const { isManager, isAdmin, isBilling, isCdp } = useAuth();
    const canViewTeamReports = isManager || isAdmin || isBilling;

    const [dateRange, setDateRange] = useState(() => {
        const now = new Date();
        return {
            start: format(startOfMonth(now), 'yyyy-MM-dd'),
            end: format(endOfMonth(now), 'yyyy-MM-dd'),
        };
    });
    const [viewMode, setViewMode] = useState<'personal' | 'team' | 'cdp'>('personal');
    const [groupBy, setGroupBy] = useState<'project' | 'user' | 'client'>('project');

    const endpointForView = {
        personal: '/api/stats/me',
        team: '/api/stats/team',
        cdp: '/api/stats/cdp',
    } as const;

    const { data: statsData, isLoading } = useQuery({
        queryKey: ['stats', viewMode, dateRange],
        queryFn: () =>
            api.get<ApiResponse<ReportStats>>(endpointForView[viewMode], {
                startDate: dateRange.start,
                endDate: dateRange.end,
            }),
    });

    const stats = statsData?.data;

    // Quick date presets
    const setPreset = (preset: 'thisMonth' | 'lastMonth' | 'last3Months') => {
        const now = new Date();
        switch (preset) {
            case 'thisMonth':
                setDateRange({
                    start: format(startOfMonth(now), 'yyyy-MM-dd'),
                    end: format(endOfMonth(now), 'yyyy-MM-dd'),
                });
                break;
            case 'lastMonth': {
                const lastMonth = subMonths(now, 1);
                setDateRange({
                    start: format(startOfMonth(lastMonth), 'yyyy-MM-dd'),
                    end: format(endOfMonth(lastMonth), 'yyyy-MM-dd'),
                });
                break;
            }
            case 'last3Months':
                setDateRange({
                    start: format(startOfMonth(subMonths(now, 2)), 'yyyy-MM-dd'),
                    end: format(endOfMonth(now), 'yyyy-MM-dd'),
                });
                break;
        }
    };

    const exportCsv = async () => {
        if (!stats) return;

        let csvContent = '';
        let filename = '';
        const periodLabel = `${format(new Date(dateRange.start), 'dd-MM-yyyy')}_${format(new Date(dateRange.end), 'dd-MM-yyyy')}`;

        switch (groupBy) {
            case 'project':
                csvContent = 'Projet;Client;Code;Heures;Jours;Heures Validées;Jours Validés\n';
                stats.byProject.forEach(p => {
                    csvContent += `"${p.projectName}";"${p.clientName}";"${p.projectCode}";${p.hours.toFixed(2)};${hoursToDays(p.hours).toFixed(2)};${p.approvedHours.toFixed(2)};${hoursToDays(p.approvedHours).toFixed(2)}\n`;
                });
                filename = `rapport_projets_${periodLabel}.csv`;
                break;

            case 'user':
                csvContent = 'Collaborateur;Heures;Jours;Heures Validées;Jours Validés;Jours Travaillés\n';
                stats.byUser.forEach(u => {
                    csvContent += `"${u.userName}";${u.hours.toFixed(2)};${hoursToDays(u.hours).toFixed(2)};${u.approvedHours.toFixed(2)};${hoursToDays(u.approvedHours).toFixed(2)};${u.daysWorked}\n`;
                });
                filename = `rapport_collaborateurs_${periodLabel}.csv`;
                break;

            case 'client':
                csvContent = 'Client;Heures;Jours;Nombre de Projets\n';
                stats.byClient.forEach(c => {
                    csvContent += `"${c.clientName}";${c.hours.toFixed(2)};${hoursToDays(c.hours).toFixed(2)};${c.projectCount}\n`;
                });
                filename = `rapport_clients_${periodLabel}.csv`;
                break;
        }

        // Add BOM for Excel UTF-8 compatibility
        const BOM = '\uFEFF';
        const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-[var(--color-text)]">Rapports</h1>
                    <p className="text-[var(--color-text-secondary)]">
                        {format(new Date(dateRange.start), 'd MMM', { locale: fr })} -{' '}
                        {format(new Date(dateRange.end), 'd MMM yyyy', { locale: fr })}
                    </p>
                </div>

                <button
                    onClick={exportCsv}
                    className="btn-secondary"
                >
                    <Download className="w-4 h-4" />
                    Exporter CSV
                </button>
            </div>

            {/* Filters */}
            <div className="card">
                <div className="flex flex-col lg:flex-row gap-4">
                    {/* View mode toggle */}
                    {(canViewTeamReports || isCdp) && (
                        <div className="flex rounded-lg border border-[var(--color-border)] overflow-hidden">
                            <button
                                onClick={() => setViewMode('personal')}
                                className={cn(
                                    'px-4 py-2 text-sm font-medium transition-colors',
                                    viewMode === 'personal'
                                        ? 'bg-[var(--color-primary)] text-white'
                                        : 'bg-[var(--color-bg-card)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg)]'
                                )}
                            >
                                Mes données
                            </button>
                            {canViewTeamReports && (
                                <button
                                    onClick={() => setViewMode('team')}
                                    className={cn(
                                        'px-4 py-2 text-sm font-medium transition-colors',
                                        viewMode === 'team'
                                            ? 'bg-[var(--color-primary)] text-white'
                                            : 'bg-[var(--color-bg-card)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg)]'
                                    )}
                                >
                                    Équipe
                                </button>
                            )}
                            {isCdp && (
                                <button
                                    onClick={() => setViewMode('cdp')}
                                    className={cn(
                                        'px-4 py-2 text-sm font-medium transition-colors',
                                        viewMode === 'cdp'
                                            ? 'bg-[var(--color-primary)] text-white'
                                            : 'bg-[var(--color-bg-card)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg)]'
                                    )}
                                >
                                    Projets pilotés
                                </button>
                            )}
                        </div>
                    )}

                    {/* Date range */}
                    <div className="flex flex-wrap items-center gap-2">
                        <Calendar className="w-4 h-4 text-[var(--color-text-muted)]" />
                        <input
                            type="date"
                            value={dateRange.start}
                            onChange={(e) => setDateRange((d) => ({ ...d, start: e.target.value }))}
                            className="input text-sm"
                        />
                        <span className="text-[var(--color-text-muted)]">—</span>
                        <input
                            type="date"
                            value={dateRange.end}
                            onChange={(e) => setDateRange((d) => ({ ...d, end: e.target.value }))}
                            className="input text-sm"
                        />
                    </div>

                    {/* Quick presets */}
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setPreset('thisMonth')}
                            className="px-3 py-2 text-sm text-[var(--color-text-secondary)] hover:bg-[var(--color-bg)] rounded-lg transition-colors"
                        >
                            Ce mois
                        </button>
                        <button
                            onClick={() => setPreset('lastMonth')}
                            className="px-3 py-2 text-sm text-[var(--color-text-secondary)] hover:bg-[var(--color-bg)] rounded-lg transition-colors"
                        >
                            Mois dernier
                        </button>
                        <button
                            onClick={() => setPreset('last3Months')}
                            className="px-3 py-2 text-sm text-[var(--color-text-secondary)] hover:bg-[var(--color-bg)] rounded-lg transition-colors"
                        >
                            3 derniers mois
                        </button>
                    </div>
                </div>
            </div>

            {isLoading ? (
                <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-[var(--color-primary)]" />
                </div>
            ) : stats ? (
                <>
                    {/* Summary cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <StatCard
                            icon={Clock}
                            label="Total jours"
                            value={`${hoursToDays(stats.totalHours)}j`}
                            color="blue"
                        />
                        <StatCard
                            icon={TrendingUp}
                            label="Jours validés"
                            value={`${hoursToDays(stats.approvedHours)}j`}
                            color="green"
                        />
                        <StatCard
                            icon={BarChart2}
                            label="En attente"
                            value={`${hoursToDays(stats.pendingHours)}j`}
                            color="yellow"
                        />
                        <StatCard
                            icon={PieChart}
                            label="Facturés"
                            value={`${hoursToDays(stats.billedHours)}j`}
                            color="purple"
                        />
                    </div>

                    {/* Group by toggle */}
                    {(viewMode === 'team' || viewMode === 'cdp') && (
                        <div className="flex items-center gap-2">
                            <Filter className="w-4 h-4 text-[var(--color-text-muted)]" />
                            <span className="text-sm text-[var(--color-text-secondary)]">Grouper par:</span>
                            <div className="flex rounded-lg border border-[var(--color-border)] overflow-hidden">
                                <button
                                    onClick={() => setGroupBy('project')}
                                    className={cn(
                                        'px-3 py-1.5 text-sm transition-colors',
                                        groupBy === 'project'
                                            ? 'bg-[var(--color-bg-hover)] text-[var(--color-text)]'
                                            : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-bg)]'
                                    )}
                                >
                                    Projet
                                </button>
                                <button
                                    onClick={() => setGroupBy('user')}
                                    className={cn(
                                        'px-3 py-1.5 text-sm transition-colors',
                                        groupBy === 'user'
                                            ? 'bg-[var(--color-bg-hover)] text-[var(--color-text)]'
                                            : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-bg)]'
                                    )}
                                >
                                    Collaborateur
                                </button>
                                <button
                                    onClick={() => setGroupBy('client')}
                                    className={cn(
                                        'px-3 py-1.5 text-sm transition-colors',
                                        groupBy === 'client'
                                            ? 'bg-[var(--color-bg-hover)] text-[var(--color-text)]'
                                            : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-bg)]'
                                    )}
                                >
                                    Client
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Data table */}
                    <div className="card overflow-hidden">
                        {groupBy === 'project' && stats.byProject && (
                            <ProjectTable data={stats.byProject} />
                        )}
                        {groupBy === 'user' && stats.byUser && (
                            <UserTable data={stats.byUser} dateRange={dateRange} enableDrilldown={viewMode === 'team'} />
                        )}
                        {groupBy === 'client' && stats.byClient && (
                            <ClientTable data={stats.byClient} />
                        )}
                    </div>
                </>
            ) : (
                <div className="text-center py-12 card">
                    <p className="text-[var(--color-text-secondary)]">Aucune donnée disponible</p>
                </div>
            )}
        </div>
    );
}

interface StatCardProps {
    icon: React.ElementType;
    label: string;
    value: string;
    color: 'blue' | 'green' | 'yellow' | 'purple';
}

function StatCard({ icon: Icon, label, value, color }: StatCardProps) {
    const colorClasses = {
        blue: 'bg-[var(--color-primary-light)] text-[var(--color-primary)]',
        green: 'bg-[var(--color-success-light)] text-[var(--color-success)]',
        yellow: 'bg-[var(--color-warning-light)] text-[var(--color-warning)]',
        purple: 'bg-purple-50 text-purple-600',
    };

    return (
        <div className="card">
            <div className="flex items-center gap-3">
                <div className={cn('p-2 rounded-lg', colorClasses[color])}>
                    <Icon className="w-5 h-5" />
                </div>
                <div>
                    <p className="text-sm text-[var(--color-text-secondary)]">{label}</p>
                    <p className="text-xl font-bold text-[var(--color-text)]">{value}</p>
                </div>
            </div>
        </div>
    );
}

function ProjectTable({ data }: { data: ReportStats['byProject'] }) {
    return (
        <div className="overflow-x-auto">
            <table className="w-full min-w-[500px]">
                <thead className="bg-[var(--color-bg)]">
                    <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider">
                            Projet
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider">
                            Client
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider">
                            Jours
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider">
                            Validés
                        </th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-[var(--color-border)]">
                    {data.map((row) => (
                        <tr key={row.projectId} className="hover:bg-[var(--color-bg)]">
                            <td className="px-4 py-3">
                                <span className="font-medium text-[var(--color-text)]">{row.projectCode}</span>
                                <span className="text-[var(--color-text-secondary)] ml-2">{row.projectName}</span>
                            </td>
                            <td className="px-4 py-3 text-[var(--color-text-secondary)]">{row.clientName}</td>
                            <td className="px-4 py-3 text-right font-medium text-[var(--color-text)]">
                                {hoursToDays(row.hours)}j
                            </td>
                            <td className="px-4 py-3 text-right text-[var(--color-success)]">
                                {hoursToDays(row.approvedHours)}j
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

interface UserTableProps {
    data: ReportStats['byUser'];
    dateRange: { start: string; end: string };
    enableDrilldown?: boolean;
}

function UserTable({ data, dateRange, enableDrilldown = true }: UserTableProps) {
    const api = useApi();
    const [selectedUser, setSelectedUser] = useState<{ userId: string; userName: string } | null>(null);

    const { data: userEntries, isLoading: loadingEntries } = useQuery({
        queryKey: ['userEntries', selectedUser?.userId, dateRange],
        queryFn: () =>
            api.get<ApiResponse<TimeEntry[]>>('/api/time-entries', {
                userId: selectedUser!.userId,
                startDate: dateRange.start,
                endDate: dateRange.end,
            }),
        enabled: !!selectedUser,
    });

    // Group entries by date
    const entriesByDate = userEntries?.data?.reduce((acc, entry) => {
        const dateKey = entry.entryDate.split('T')[0];
        if (!acc[dateKey]) {
            acc[dateKey] = [];
        }
        acc[dateKey].push(entry);
        return acc;
    }, {} as Record<string, TimeEntry[]>) || {};

    // Generate all days in range
    const allDays = selectedUser ? eachDayOfInterval({
        start: parseISO(dateRange.start),
        end: parseISO(dateRange.end),
    }) : [];

    return (
        <>
            <div className="overflow-x-auto">
                <table className="w-full min-w-[500px]">
                    <thead className="bg-[var(--color-bg)]">
                        <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider">
                                Collaborateur
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider">
                                Jours travaillés
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider">
                                Jours
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider">
                                Validés
                            </th>
                            {enableDrilldown && <th className="px-4 py-3 w-10"></th>}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-[var(--color-border)]">
                        {data.map((row) => (
                            <tr
                                key={row.userId}
                                className={cn(
                                    'transition-colors',
                                    enableDrilldown && 'hover:bg-[var(--color-bg)] cursor-pointer'
                                )}
                                onClick={enableDrilldown ? () => setSelectedUser({ userId: row.userId, userName: row.userName }) : undefined}
                            >
                                <td className="px-4 py-3">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-[var(--color-primary-light)] flex items-center justify-center">
                                            <span className="text-[var(--color-primary)] text-sm font-medium">
                                                {row.userName
                                                    .split(' ')
                                                    .map((n) => n[0])
                                                    .join('')
                                                    .toUpperCase()
                                                    .slice(0, 2)}
                                            </span>
                                        </div>
                                        <span className="font-medium text-[var(--color-text)]">{row.userName}</span>
                                    </div>
                                </td>
                                <td className="px-4 py-3 text-right text-[var(--color-text-secondary)]">{row.daysWorked}</td>
                                <td className="px-4 py-3 text-right font-medium text-[var(--color-text)]">
                                    {hoursToDays(row.hours)}j
                                </td>
                                <td className="px-4 py-3 text-right text-[var(--color-success)]">
                                    {hoursToDays(row.approvedHours)}j
                                </td>
                                {enableDrilldown && (
                                    <td className="px-4 py-3">
                                        <ChevronRight className="w-4 h-4 text-[var(--color-text-muted)]" />
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* User Detail Modal */}
            {selectedUser && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-[var(--color-bg-card)] rounded-xl shadow-xl max-w-4xl w-full max-h-[85vh] overflow-hidden">
                        {/* Modal Header */}
                        <div className="flex items-center justify-between p-4 border-b border-[var(--color-border)]">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-[var(--color-primary-light)] flex items-center justify-center">
                                    <span className="text-[var(--color-primary)] font-medium">
                                        {selectedUser.userName
                                            .split(' ')
                                            .map((n) => n[0])
                                            .join('')
                                            .toUpperCase()
                                            .slice(0, 2)}
                                    </span>
                                </div>
                                <div>
                                    <h2 className="text-lg font-semibold text-[var(--color-text)]">
                                        {selectedUser.userName}
                                    </h2>
                                    <p className="text-sm text-[var(--color-text-secondary)]">
                                        {format(parseISO(dateRange.start), 'd MMM', { locale: fr })} - {format(parseISO(dateRange.end), 'd MMM yyyy', { locale: fr })}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => setSelectedUser(null)}
                                className="p-2 hover:bg-[var(--color-bg)] rounded-lg transition-colors"
                            >
                                <X className="w-5 h-5 text-[var(--color-text-muted)]" />
                            </button>
                        </div>

                        {/* Modal Content */}
                        <div className="p-4 overflow-y-auto max-h-[calc(85vh-80px)]">
                            {loadingEntries ? (
                                <div className="flex items-center justify-center py-12">
                                    <Loader2 className="w-8 h-8 animate-spin text-[var(--color-primary)]" />
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {allDays.map((day) => {
                                        const dateKey = format(day, 'yyyy-MM-dd');
                                        const dayEntries = entriesByDate[dateKey] || [];
                                        const dayTotal = dayEntries.reduce((sum, e) => sum + e.hours, 0);
                                        const isWeekend = day.getDay() === 0 || day.getDay() === 6;

                                        return (
                                            <div
                                                key={dateKey}
                                                className={cn(
                                                    'border border-[var(--color-border)] rounded-lg overflow-hidden',
                                                    isWeekend && 'opacity-60'
                                                )}
                                            >
                                                <div className={cn(
                                                    'flex items-center justify-between px-4 py-2',
                                                    dayEntries.length > 0 ? 'bg-[var(--color-primary-light)]' : 'bg-[var(--color-bg)]'
                                                )}>
                                                    <span className="font-medium text-[var(--color-text)]">
                                                        {format(day, 'EEEE d MMMM', { locale: fr })}
                                                    </span>
                                                    <span className={cn(
                                                        'font-bold',
                                                        dayTotal > 0 ? 'text-[var(--color-primary)]' : 'text-[var(--color-text-muted)]'
                                                    )}>
                                                        {hoursToDays(dayTotal)}j
                                                    </span>
                                                </div>
                                                {dayEntries.length > 0 && (
                                                    <div className="divide-y divide-[var(--color-border)]">
                                                        {dayEntries.map((entry) => (
                                                            <div key={entry.id} className="px-4 py-2 bg-[var(--color-bg-card)] flex items-center justify-between">
                                                                <div className="flex-1">
                                                                    <div className="flex items-center gap-2">
                                                                        <span className="font-medium text-[var(--color-text)]">
                                                                            {entry.projectCode || 'N/A'}
                                                                        </span>
                                                                        <span className={getStatusBadgeClass(entry.status)}>
                                                                            {getStatusLabel(entry.status)}
                                                                        </span>
                                                                    </div>
                                                                    {(entry.projectName || entry.clientName) && (
                                                                        <p className="text-sm text-[var(--color-text-secondary)] mt-0.5">
                                                                            {entry.projectName}
                                                                            {entry.projectName && entry.clientName ? ' · ' : ''}
                                                                            {entry.clientName}
                                                                        </p>
                                                                    )}
                                                                    {entry.description && (
                                                                        <p className="text-sm text-[var(--color-text-secondary)] mt-1">
                                                                            {entry.description}
                                                                        </p>
                                                                    )}
                                                                </div>
                                                                <span className="font-bold text-[var(--color-text)] ml-4">
                                                                    {hoursToDays(entry.hours)}j
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

function ClientTable({ data }: { data: ReportStats['byClient'] }) {
    return (
        <div className="overflow-x-auto">
            <table className="w-full min-w-[400px]">
                <thead className="bg-[var(--color-bg)]">
                    <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider">
                            Client
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider">
                            Projets
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider">
                            Jours
                        </th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-[var(--color-border)]">
                    {data.map((row) => (
                        <tr key={row.clientId} className="hover:bg-[var(--color-bg)]">
                            <td className="px-4 py-3">
                                <div className="flex items-center gap-3">
                                    <Building2 className="w-5 h-5 text-[var(--color-text-muted)]" />
                                    <span className="font-medium text-[var(--color-text)]">{row.clientName}</span>
                                </div>
                            </td>
                            <td className="px-4 py-3 text-right text-[var(--color-text-secondary)]">
                                {row.projectCount}
                            </td>
                            <td className="px-4 py-3 text-right font-medium text-[var(--color-text)]">
                                {hoursToDays(row.hours)}j
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
